/**
 * Writing to the register — 16 Sep 2026.
 *
 * The newsletter's shape, addressed at the cap table: pick who, write, see it
 * merged for a real recipient, test it, send it. `PaymentRequestModal` beside
 * this does the one send that carries money; this does every other one.
 *
 * ## The audience is picked as a group, not as a list
 *
 * Four checkboxes, and each is a status the register derives from its ledgers
 * rather than a label anybody maintains: who holds shares, who holds options
 * and no shares, who is on the register having never held either, who held once
 * and holds none now. Somebody who bought shares this morning is in the first
 * group this afternoon without anyone updating a list.
 *
 * A round may be named as well, to write to the people with a position in it —
 * "everyone who took part in Emisjon 4" — and the rows may be unticked one at a
 * time. Both only ever narrow.
 *
 * The list shown here is computed from the same ledgers the server resolves
 * from, so what is on the screen is what will be written to. The server
 * resolves it again anyway: a browser says which groups, never which addresses.
 *
 * ## The room goes to the message
 *
 * Two columns, and the writing one is nearly twice the width of the list of
 * people it is going to: a letter is written once and the recipients are
 * checked at a glance. Inside the writing column only the editor grows, so the
 * formatting toolbar stays where a writer left it however long the message
 * gets — a toolbar that scrolls out of reach is a toolbar you stop using.
 *
 * The preview opens on a button. Standing open it took a third of the column
 * to show something a writer looks at twice.
 *
 * ## Rich text, and only as much of it as email survives
 *
 * The newsletter's editor, unchanged: bold, italics, underline, a bullet list
 * and a link. Headings, colours and font sizes are not offered, because they
 * render differently in every mail client — and a letter to investors that
 * looks broken in Outlook is worse than one that looks plain everywhere.
 *
 * The preview renders that markup rather than showing it. It is the writer's
 * own HTML, from the editor beside it, and there is no path by which a
 * recipient's text reaches it — the merge fields are names, addresses and share
 * counts out of the register.
 *
 * ## Attachments
 *
 * Up to 1 MB in total, matching the newsletter's ceiling. An attachment is sent
 * once per recipient, so it is a size limit about the send rather than about
 * the file.
 *
 * ## Drafts
 *
 * Saved to `skidaCrmEmailSends` as the newsletter's are, and listed in the log
 * beside the sends so there is one place where "what have we written to these
 * people" is answered. The draft carries the words and the audience; it does
 * not carry the attachments, which are base64 and would not fit in a Firestore
 * document — the file list says so rather than letting somebody find out by
 * reopening one.
 *
 * A draft is deleted once its letter has gone out. Leaving it would put the
 * same message in the log twice, once as a thing that happened and once as a
 * thing somebody still means to do.
 */
import { useMemo, useRef, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { RichText } from '../newsletter/RichText'
import { callFunction } from '@/shared/firebase/callFunction'
import { fmtFileSize } from '@/shared/util/files'
import type { CompanyDetails } from '@/types/company'
import {
  createShareholderDraft,
  deleteShareholderDraft,
  updateShareholderDraft,
} from './repository/ownershipRepository'
import {
  HOLDER_STATUSES,
  holderStatus,
  netShares,
  type HolderStatus,
  type Issuance,
  type ShareholderSend,
  type ShareTransfer,
  type Shareholder,
} from '@/types/ownership'
import './Mail.css'

const nb = (n: number) => n.toLocaleString('nb-NO')

/** Total bytes of attachment this send may carry. Mirrors the send function. */
const MAX_ATTACHMENT_BYTES = 1024 * 1024

const TEMPLATES = {
  no: {
    subject: 'Oppdatering fra {{selskap}}',
    body:
      '<p>Hei {{navn}},</p>' +
      '<p>Her kommer en oppdatering fra {{selskap}}.</p>' +
      '<p>Du står registrert med {{antall}} aksjer.</p>' +
      '<p>Med vennlig hilsen<br>{{selskap}}</p>',
  },
  en: {
    subject: 'An update from {{company}}',
    body:
      '<p>Hi {{name}},</p>' +
      '<p>Here is an update from {{company}}.</p>' +
      '<p>You are registered with {{shares}} shares.</p>' +
      '<p>Kind regards,<br>{{company}}</p>',
  },
}

/** What the compose screen tells the writer they may use. */
const TAGS = {
  no: '{{navn}} {{epost}} {{antall}} {{selskap}}',
  en: '{{name}} {{email}} {{shares}} {{company}}',
}

interface Row {
  id: string
  name: string
  email: string
  shares: number
  status: HolderStatus
}

interface File {
  filename: string
  /** Base64, no data-url prefix — what Resend takes. */
  content: string
  bytes: number
}

export function ShareholderMailModal({
  holders,
  transfers,
  issuances,
  me,
  company,
  draft,
  onClose,
}: {
  holders: Shareholder[]
  transfers: ShareTransfer[]
  issuances: Issuance[]
  /** The signed-in address — where a test goes. */
  me: string
  /** For `{{selskap}}` in the body. */
  company: CompanyDetails
  /** A saved draft being reopened, where one is. */
  draft?: ShareholderSend
  onClose: () => void
}) {
  const [lang, setLang] = useState<'no' | 'en'>('no')
  const [subject, setSubject] = useState(draft?.subject ?? TEMPLATES.no.subject)
  const [body, setBody] = useState(draft?.html || TEMPLATES.no.body)
  const [groups, setGroups] = useState<HolderStatus[]>(
    draft && draft.groups.length ? draft.groups : ['shareholder'],
  )
  const [issuanceId, setIssuanceId] = useState(draft?.issuanceId ?? '')
  /** The draft this is, once saved — so a second save updates rather than adds. */
  const [draftId, setDraftId] = useState(draft?.id ?? '')
  const [saved, setSaved] = useState('')
  const [dropped, setDropped] = useState<string[]>([])
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState<'test' | 'live' | 'draft' | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tested, setTested] = useState('')
  /** The preview is a thing you open — it was taking a third of the screen. */
  const [previewing, setPreviewing] = useState(false)
  const filePicker = useRef<HTMLInputElement>(null)

  /** Everyone the chosen groups resolve to, before anything is unticked. */
  const rows = useMemo((): Row[] => {
    return holders
      .map((h) => ({
        id: h.id,
        name: h.name,
        email: (h.email || '').trim(),
        shares: netShares(h, transfers),
        status: holderStatus(h, transfers),
      }))
      .filter((r) => groups.includes(r.status))
      .filter((r) => {
        if (!issuanceId) return true
        const h = holders.find((x) => x.id === r.id)!
        // Either ledger: a stake that came through exercising an option is a
        // position in that round as much as a purchase is.
        return (
          h.purchases.some((p) => p.issuanceId === issuanceId) ||
          h.options.some((o) => o.exerciseIssuanceId === issuanceId && o.status === 'exercised')
        )
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'nb'))
  }, [holders, transfers, groups, issuanceId])

  const included = rows.filter((r) => !dropped.includes(r.id) && r.email.includes('@'))
  const noAddress = rows.filter((r) => !r.email.includes('@'))

  const merge = (template: string, r: Row) =>
    template.replace(/\{\{([A-Za-zÆØÅæøå0-9_-]+)\}\}/g, (whole, key: string) => {
      const values: Record<string, string> = {
        name: r.name,
        navn: r.name,
        email: r.email,
        epost: r.email,
        'e-post': r.email,
        shares: nb(r.shares),
        antall: nb(r.shares),
        company: company.legalName,
        selskap: company.legalName,
      }
      return key in values ? values[key]! : whole
    })

  const sample = included[0] ?? rows[0] ?? null
  const attachmentBytes = files.reduce((n, f) => n + f.bytes, 0)
  /** What a test proves is only true while the text is unchanged. */
  const fingerprint = `${subject}|${body}|${files.map((f) => f.filename).join(',')}`

  const pickLang = (next: 'no' | 'en') => {
    setLang(next)
    // Only when the text is still a template — never over something written.
    if (subject === TEMPLATES[lang].subject) setSubject(TEMPLATES[next].subject)
    if (body === TEMPLATES[lang].body) setBody(TEMPLATES[next].body)
  }

  const toggleGroup = (key: HolderStatus) =>
    setGroups((g) => (g.includes(key) ? g.filter((x) => x !== key) : [...g, key]))

  const addFiles = async (picked: FileList | null) => {
    if (!picked?.length) return
    setError(null)
    const next: File[] = [...files]
    for (const f of Array.from(picked)) {
      const buf = await f.arrayBuffer()
      let binary = ''
      const bytes = new Uint8Array(buf)
      // Chunked: `String.fromCharCode(...bytes)` blows the argument limit on a
      // file of any size, which is a crash rather than a rejected attachment.
      for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
      }
      next.push({ filename: f.name, content: btoa(binary), bytes: f.size })
    }
    const total = next.reduce((n, f) => n + f.bytes, 0)
    if (total > MAX_ATTACHMENT_BYTES) {
      setError(
        `That would be ${fmtFileSize(total)} of attachments. The limit is 1 MB — link to anything larger.`,
      )
      return
    }
    setFiles(next)
  }

  /** The draft's own content, so a save and a reopen agree on what a draft is. */
  const draftBody = { subject, html: body, groups, issuanceId, savedBy: me }

  const saveDraft = async () => {
    setBusy('draft')
    setError(null)
    setNote(null)
    try {
      if (draftId) await updateShareholderDraft(draftId, draftBody)
      else setDraftId(await createShareholderDraft(draftBody))
      setSaved(fingerprint)
      setNote('Draft saved.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The draft could not be saved.')
    } finally {
      setBusy(null)
    }
  }

  const send = async (mode: 'test' | 'live') => {
    setBusy(mode)
    setError(null)
    setNote(null)
    try {
      const out = await callFunction<{
        sent?: number
        failed?: number
        ok?: boolean
        skipped?: { noEmail: number; suppressed: number }
      }>('crmSendShareholderMail', {
        method: 'POST',
        body: {
          mode,
          testTo: me,
          subject,
          body,
          groups,
          issuanceId,
          shareholderIds: included.map((r) => r.id),
          attachments: files.map(({ filename, content }) => ({ filename, content })),
        },
      })
      if (mode === 'test') {
        setTested(fingerprint)
        setNote(`Test sent to ${me}.`)
      } else {
        const skipped =
          (out.skipped?.noEmail ?? 0) + (out.skipped?.suppressed ?? 0) > 0
            ? ` ${out.skipped?.noEmail ?? 0} without an address, ${out.skipped?.suppressed ?? 0} suppressed.`
            : ''
        setNote(`Sent to ${out.sent ?? 0}${out.failed ? `, ${out.failed} failed` : ''}.${skipped}`)
        // The letter has gone; the note about writing it should not outlive it.
        // A failure to tidy up is not a failure to send, so it is swallowed.
        if (draftId) {
          await deleteShareholderDraft(draftId).catch(() => {})
          setDraftId('')
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The send failed.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Modal
      title="Email the register"
      onClose={onClose}
      width={1170}
      /* A half-written letter to the shareholders is not something a stray
         click on the backdrop should throw away. The ✕ is the way out. */
      dismiss="explicit"
      /* The two panes below scroll on their own, which needs the dialog to
         have a height rather than to grow with its content. */
      className="modal--tall"
      footer={
        <>
          <span className="payreq__count">
            {included.length} recipient{included.length === 1 ? '' : 's'}
            {files.length > 0 && ` · ${fmtFileSize(attachmentBytes)} attached`}
          </span>
          <button
            className="btn btn--ghost"
            disabled={!sample}
            onClick={() => setPreviewing(true)}
            title={sample ? `Merged for ${sample.name}` : 'Nobody to merge against yet'}
          >
            Preview
          </button>
          <button className="btn btn--ghost" disabled={busy !== null} onClick={() => void saveDraft()}>
            {busy === 'draft' ? 'Saving…' : saved === fingerprint ? 'Draft saved' : 'Save draft'}
          </button>
          <button
            className="btn btn--ghost"
            disabled={busy !== null}
            onClick={() => void send('test')}
          >
            {busy === 'test' ? 'Sending…' : 'Send test to me'}
          </button>
          <button
            className="btn btn--primary"
            disabled={busy !== null || included.length === 0}
            onClick={() => void send('live')}
          >
            {busy === 'live' ? 'Sending…' : `Send to ${included.length}`}
          </button>
        </>
      }
    >
      <div className="payreq shmail shmail--split">
        <div className="payreq__cols">
          <div className="payreq__compose">
            <div className="payreq__lang">
              <span className="card-label">Language</span>
              <button className="chip" aria-pressed={lang === 'no'} onClick={() => pickLang('no')}>
                Norsk
              </button>
              <button className="chip" aria-pressed={lang === 'en'} onClick={() => pickLang('en')}>
                English
              </button>
            </div>

            <label className="field">
              <span className="card-label">Subject</span>
              <input
                className="input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>
            {/* The one element that grows. Everything else in this column keeps
                its own height, so the formatting toolbar stays put and the text
                scrolls under it rather than the whole column scrolling away. */}
            <div className="field shmail__bodyfield">
              <span className="card-label">Body</span>
              {/* Keyed on the language so swapping template takes in the
                  editor, which otherwise owns its own content. */}
              <RichText value={body} onChange={setBody} resetKey={lang} />
            </div>
            <p className="micro">
              Merge fields: <code>{TAGS[lang]}</code>
            </p>

            <div className="shmail__files">
              <span className="card-label">Attachments</span>
              {files.map((f, i) => (
                <div className="shmail__file" key={`${f.filename}-${i}`}>
                  <span className="shmail__filename">{f.filename}</span>
                  <span className="micro">{fmtFileSize(f.bytes)}</span>
                  <button
                    className="btn btn--ghost btn--tiny"
                    onClick={() => setFiles(files.filter((_, n) => n !== i))}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <input
                ref={filePicker}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  void addFiles(e.target.files)
                  e.target.value = ''
                }}
              />
              <button className="btn btn--ghost" onClick={() => filePicker.current?.click()}>
                + Add attachment
              </button>
              <p className="micro">
                Up to 1 MB in total — an attachment is sent once per recipient, so link to anything
                larger. Attachments are not kept with a saved draft.
              </p>
            </div>
          </div>

          <div className="payreq__side">
            <div className="shmail__groups">
              <span className="card-label">Recipient groups</span>
              <div className="shmail__chips">
                {HOLDER_STATUSES.map((s) => (
                  <button
                    key={s.key}
                    className="chip"
                    aria-pressed={groups.includes(s.key)}
                    onClick={() => toggleGroup(s.key)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <label className="field shmail__round">
                <span className="card-label">Only holders with a position in</span>
                <select
                  className="input"
                  value={issuanceId}
                  onChange={(e) => setIssuanceId(e.target.value)}
                >
                  <option value="">Any round</option>
                  {issuances.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="payreq__list">
              <div className="payreq__listhead">
                <span className="card-label">Recipients</span>
                <span className="micro">{included.length} selected</span>
              </div>
              {rows.map((r) => {
                const usable = r.email.includes('@')
                return (
                  <label className="payreq__row" key={r.id}>
                    <input
                      type="checkbox"
                      checked={usable && !dropped.includes(r.id)}
                      disabled={!usable}
                      onChange={() =>
                        setDropped((d) =>
                          d.includes(r.id) ? d.filter((x) => x !== r.id) : [...d, r.id],
                        )
                      }
                    />
                    <span className="payreq__who">
                      {r.name}
                      <span className="micro">{usable ? r.email : 'no address'}</span>
                    </span>
                    <span className="payreq__amt">{nb(r.shares)}</span>
                  </label>
                )
              })}
              {rows.length === 0 && (
                <p className="empty-note">
                  Nobody on the register is in {groups.length === 1 ? 'that group' : 'those groups'}
                  {issuanceId ? ' with a position in that round' : ''}.
                </p>
              )}
            </div>

            {noAddress.length > 0 && (
              <p className="recform__warn">
                {noAddress.length} without an email address, so nothing can be sent to them.
              </p>
            )}

          </div>
        </div>

        {tested !== fingerprint && (
          <p className="recform__note">
            This text has not been tested. A test goes to {me} merged for{' '}
            {sample ? sample.name : 'a sample recipient'}.
          </p>
        )}
        {note && <p className="recform__note payreq__done">{note}</p>}
        {error && <p className="recform__error">{error}</p>}
      </div>

      {/* Opened rather than always shown. It was standing under the recipient
          list taking a third of the column, which is a lot of room for
          something a writer looks at twice. */}
      {previewing && sample && (
        <Modal
          title={`Preview — ${sample.name}`}
          subtitle="Merged as this recipient will receive it"
          onClose={() => setPreviewing(false)}
          width={680}
          footer={
            <button className="btn btn--ghost" onClick={() => setPreviewing(false)}>
              Close
            </button>
          }
        >
          <div className="shmail__previewdoc">
            <div className="shmail__previewsub">{merge(subject, sample)}</div>
            {/* The writer's own markup, from the editor behind this dialog. */}
            <div
              className="shmail__previewbody"
              dangerouslySetInnerHTML={{ __html: merge(body, sample) }}
            />
            {files.length > 0 && (
              <p className="micro">
                {files.length} attachment{files.length === 1 ? '' : 's'}:{' '}
                {files.map((f) => f.filename).join(', ')}
              </p>
            )}
          </div>
        </Modal>
      )}
    </Modal>
  )
}
